'use client';

import React, { useState, useMemo } from 'react';
import { Heart, MessageCircle, Send, Film, ChevronDown, ChevronUp, CheckCircle, Flame, Clock, Sparkles, Plus, X, Package, Paperclip, Play, Search, Link2 } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { useClips } from '@/lib/useClips';
import type { ClipData } from '@/components/ClipCard';
import CategoryIcon from '@/components/ui/CategoryIcon';


interface ClipAttachment {
  type: 'clip' | 'scenepack';
  title: string;
  thumbnail: string;
  resolution: string;
  duration: string;
  clipCount?: number;
  linkedId?: string; // id of the existing platform item if linked
}

interface Comment {
  id: string;
  author: string;
  avatar: string;
  verified: boolean;
  text: string;
  likes: number;
  liked: boolean;
  timeAgo: string;
  attachment?: ClipAttachment;
}

interface ClipRequest {
  id: string;
  author: string;
  avatar: string;
  verified: boolean;
  title: string;
  description: string;
  tags: string[];
  category: string;
  likes: number;
  liked: boolean;
  commentCount: number;
  timeAgo: string;
  status: 'open' | 'fulfilled';
  comments: Comment[];
}

const CATEGORIES = ['All', 'Action', 'Anime', 'Cinematic', 'VFX', 'Gaming', 'Nature', 'Cars', 'Horror', 'Sports'];

function formatCount(n: number): string {
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toString();
}

// ─── Link Existing Picker ────────────────────────────────────────────────────

interface LinkExistingPickerProps {
  onSelect: (attachment: ClipAttachment) => void;
  onCancel: () => void;
}

function LinkExistingPicker({ onSelect, onCancel }: LinkExistingPickerProps) {
  const [query, setQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'clip' | 'scenepack'>('all');

  // Fetch clips from the platform
  const { clips, loading } = useClips({ limit: 50 });

  // Build a unified list (clips only for now; scenepacks would come from a separate hook)
  const allItems: ClipAttachment[] = useMemo(() => {
    return clips.map((c: ClipData) => ({
      type: 'clip' as const,
      title: c.title ?? 'Untitled Clip',
      thumbnail: c.thumbnail ?? '',
      resolution: c.resolution ?? '1080p',
      duration: c.duration ?? '',
      linkedId: c.id,
    }));
  }, [clips]);

  const filtered = useMemo(() => {
    return allItems.filter((item) => {
      const matchesType = filterType === 'all' || item.type === filterType;
      const matchesQuery = !query.trim() || item.title.toLowerCase().includes(query.toLowerCase());
      return matchesType && matchesQuery;
    });
  }, [allItems, query, filterType]);

  return (
    <div className="rounded-xl p-3 mb-2 space-y-2.5" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold" style={{ color: 'var(--foreground)' }}>Link from Platform</span>
        <button onClick={onCancel} className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: 'var(--border)', color: 'var(--muted-foreground)' }}>
          <X size={10} />
        </button>
      </div>

      {/* Type filter */}
      <div className="flex gap-1.5">
        {(['all', 'clip', 'scenepack'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setFilterType(t)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all capitalize"
            style={{
              background: filterType === t ? 'var(--primary)' : 'var(--card)',
              color: filterType === t ? '#fff' : 'var(--muted-foreground)',
              border: '1px solid var(--border)',
            }}
          >
            {t === 'clip' && <Play size={9} />}
            {t === 'scenepack' && <Package size={9} />}
            {t === 'all' ? 'All' : t === 'clip' ? 'Clips' : 'Scenepacks'}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search clips & scenepacks…"
          className="input-field w-full py-1.5 pl-7 pr-2.5 text-xs"
          autoFocus
        />
      </div>

      {/* Results */}
      <div className="max-h-48 overflow-y-auto space-y-1.5 pr-0.5" style={{ scrollbarWidth: 'thin' }}>
        {loading && (
          <div className="flex flex-col gap-1.5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 rounded-lg animate-pulse" style={{ background: 'var(--card)' }} />
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="py-6 text-center">
            <Film size={20} className="mx-auto mb-2" style={{ color: 'var(--muted-foreground)' }} />
            <p className="text-[10px] font-semibold mb-0.5" style={{ color: 'var(--foreground)' }}>
              {allItems.length === 0 ? 'Upload clips first to link them here' : 'Try a different search term'}
            </p>
            <p className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
              {allItems.length === 0
                ? 'Upload clips first, or enter details manually below' :'Try a different search term'}
            </p>
          </div>
        )}

        {!loading && filtered.map((item, idx) => (
          <button
            key={item.linkedId ?? idx}
            onClick={() => onSelect(item)}
            className="w-full flex items-center gap-2 p-2 rounded-lg text-left transition-all hover:opacity-80"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
          >
            <div className="relative flex-shrink-0 rounded-md overflow-hidden flex items-center justify-center" style={{ width: 40, height: 28, background: 'rgba(108,99,255,0.12)' }}>
              {item.thumbnail ? (
                <AppImage src={item.thumbnail} alt={item.title} fill className="object-cover" sizes="40px" />
              ) : (
                item.type === 'scenepack'
                  ? <Package size={13} style={{ color: 'var(--primary)' }} />
                  : <Play size={13} style={{ color: 'var(--primary)' }} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold truncate" style={{ color: 'var(--foreground)' }}>{item.title}</p>
              <p className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>
                <span className="capitalize">{item.type}</span>
                {item.resolution ? ` · ${item.resolution}` : ''}
                {item.duration ? ` · ${item.duration}` : ''}
              </p>
            </div>
            <Link2 size={11} style={{ color: 'var(--primary)', flexShrink: 0 }} />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Combined Attachment Form (mode selector + sub-forms) ─────────────────────

interface AttachmentFormProps {
  onAttach: (attachment: ClipAttachment) => void;
  onCancel: () => void;
}

function AttachmentForm({ onAttach, onCancel }: AttachmentFormProps) {
  return <LinkExistingPicker onSelect={onAttach} onCancel={onCancel} />;
}

// ─── Attachment Preview ───────────────────────────────────────────────────────

interface AttachmentPreviewProps {
  attachment: ClipAttachment;
  onRemove?: () => void;
  compact?: boolean;
}

function AttachmentPreview({ attachment, onRemove, compact }: AttachmentPreviewProps) {
  return (
    <div className="flex items-center gap-2 p-2 rounded-xl mb-2" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
      <div className="relative flex-shrink-0 rounded-lg overflow-hidden flex items-center justify-center" style={{ width: compact ? 40 : 48, height: compact ? 28 : 32, background: 'rgba(108,99,255,0.12)' }}>
        {attachment.thumbnail ? (
          <AppImage src={attachment.thumbnail} alt={attachment.title} fill className="object-cover" sizes="48px" />
        ) : (
          attachment.type === 'scenepack'
            ? <Package size={compact ? 14 : 16} style={{ color: 'var(--primary)' }} />
            : <Play size={compact ? 14 : 16} style={{ color: 'var(--primary)' }} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 mb-0.5">
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wide" style={{ background: attachment.type === 'scenepack' ? 'rgba(108,99,255,0.15)' : 'rgba(34,197,94,0.12)', color: attachment.type === 'scenepack' ? 'var(--primary)' : '#22c55e' }}>
            {attachment.type === 'scenepack' ? 'Scenepack' : 'Clip'}
          </span>
          {attachment.linkedId && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-wide flex items-center gap-0.5" style={{ background: 'rgba(108,99,255,0.08)', color: 'var(--primary)' }}>
              <Link2 size={7} /> Linked
            </span>
          )}
        </div>
        <p className="text-[10px] font-semibold truncate" style={{ color: 'var(--foreground)' }}>{attachment.title}</p>
        <p className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
          {attachment.resolution} · {attachment.duration}
          {attachment.type === 'scenepack' && attachment.clipCount ? ` · ${attachment.clipCount} clips` : ''}
        </p>
      </div>
      {onRemove && (
        <button onClick={onRemove} className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-colors hover:opacity-80" style={{ background: 'var(--border)', color: 'var(--muted-foreground)' }}>
          <X size={10} />
        </button>
      )}
    </div>
  );
}

// ─── Request Card ─────────────────────────────────────────────────────────────

interface RequestCardProps {
  request: ClipRequest;
  onLike: (id: string) => void;
  onCommentLike: (reqId: string, commentId: string) => void;
  onAddComment: (reqId: string, text: string, attachment?: ClipAttachment) => void;
}

function RequestCard({ request, onLike, onCommentLike, onAddComment }: RequestCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [showAttachForm, setShowAttachForm] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState<ClipAttachment | null>(null);

  const handleSubmitComment = () => {
    if (!commentText.trim() && !pendingAttachment) return;
    onAddComment(request.id, commentText.trim(), pendingAttachment ?? undefined);
    setCommentText('');
    setPendingAttachment(null);
    setShowAttachForm(false);
  };

  const handleAttach = (attachment: ClipAttachment) => {
    setPendingAttachment(attachment);
    setShowAttachForm(false);
  };

  return (
    <div className="rounded-2xl overflow-hidden transition-all duration-200" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="relative w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
            <AppImage src={request.avatar} alt={`${request.author} profile picture`} fill className="object-cover" sizes="36px" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>{request.author}</span>
              {request.verified && <CheckCircle size={13} style={{ color: 'var(--verified)', flexShrink: 0 }} />}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{request.timeAgo}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${request.status === 'fulfilled' ? 'text-green-400' : 'text-orange-400'}`}
                style={{ background: request.status === 'fulfilled' ? 'rgba(34,197,94,0.12)' : 'rgba(251,146,60,0.12)', border: `1px solid ${request.status === 'fulfilled' ? 'rgba(34,197,94,0.25)' : 'rgba(251,146,60,0.25)'}` }}>
                {request.status === 'fulfilled' ? '✓ Fulfilled' : 'Open'}
              </span>
            </div>
          </div>
          <span className="text-xs px-2 py-1 rounded-lg font-medium flex-shrink-0 flex items-center gap-1" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
            <CategoryIcon category={request.category} size={11} color={activeCategory === request.category ? '#fff' : undefined} />
            {request.category}
          </span>
        </div>

        <h3 className="text-sm font-bold mb-1.5 leading-snug" style={{ color: 'var(--foreground)' }}>{request.title}</h3>
        <p className="text-xs leading-relaxed mb-3" style={{ color: 'var(--muted-foreground)' }}>{request.description}</p>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {request.tags.map((tag) => (
            <span key={tag} className="text-[10px] font-semibold px-2 py-0.5 rounded-md" style={{ background: 'rgba(108,99,255,0.1)', color: 'var(--primary)', border: '1px solid rgba(108,99,255,0.2)' }}>
              #{tag}
            </span>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button onClick={() => onLike(request.id)} className="flex items-center gap-1.5 text-xs font-semibold transition-all duration-150 hover:scale-105" style={{ color: request.liked ? '#EF4444' : 'var(--muted-foreground)' }}>
            <Heart size={14} fill={request.liked ? '#EF4444' : 'none'} />
            {formatCount(request.likes)}
          </button>
          <button onClick={() => setExpanded((v) => !v)} className="flex items-center gap-1.5 text-xs font-semibold transition-colors hover:opacity-80" style={{ color: 'var(--muted-foreground)' }}>
            <MessageCircle size={14} />
            {request.commentCount} {request.commentCount === 1 ? 'reply' : 'replies'}
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t px-4 pb-4 pt-3 space-y-3" style={{ borderColor: 'var(--border)' }}>
          {request.comments.length === 0 && (
            <p className="text-xs text-center py-4" style={{ color: 'var(--muted-foreground)' }}>No replies yet — be the first to respond with a clip or scenepack!</p>
          )}
          {request.comments.map((comment) => (
            <div key={comment.id} className="flex gap-2.5">
              <div className="relative w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mt-0.5" style={{ background: 'var(--muted)' }}>
                {comment.avatar ? (
                  <AppImage src={comment.avatar} alt={`${comment.author} avatar`} fill className="object-cover" sizes="28px" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] font-bold" style={{ color: 'var(--muted-foreground)' }}>{comment.author.charAt(0).toUpperCase()}</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>{comment.author}</span>
                  {comment.verified && <CheckCircle size={11} style={{ color: 'var(--verified)' }} />}
                  <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{comment.timeAgo}</span>
                </div>
                {comment.text && (
                  <p className="text-xs leading-relaxed mb-1.5" style={{ color: 'var(--secondary-foreground)' }}>{comment.text}</p>
                )}
                {comment.attachment && (
                  <AttachmentPreview attachment={comment.attachment} compact />
                )}
                <button onClick={() => onCommentLike(request.id, comment.id)} className="flex items-center gap-1 text-[10px] font-semibold transition-colors" style={{ color: comment.liked ? '#EF4444' : 'var(--muted-foreground)' }}>
                  <Heart size={11} fill={comment.liked ? '#EF4444' : 'none'} />
                  {comment.likes}
                </button>
              </div>
            </div>
          ))}

          {/* Reply composer */}
          <div className="flex gap-2 pt-1">
            <div className="relative w-7 h-7 rounded-full overflow-hidden flex-shrink-0 mt-0.5" style={{ background: 'var(--muted)' }}>
              <div className="w-full h-full flex items-center justify-center text-[10px] font-bold" style={{ color: 'var(--muted-foreground)' }}>U</div>
            </div>
            <div className="flex-1 min-w-0">
              {/* Attachment form */}
              {showAttachForm && (
                <AttachmentForm onAttach={handleAttach} onCancel={() => setShowAttachForm(false)} />
              )}

              {/* Pending attachment preview */}
              {pendingAttachment && !showAttachForm && (
                <AttachmentPreview attachment={pendingAttachment} onRemove={() => setPendingAttachment(null)} />
              )}

              <div className="flex gap-2">
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) handleSubmitComment(); }}
                  placeholder="Reply with a comment…"
                  className="input-field flex-1 py-1.5 px-3 text-xs"
                />
                {/* Attach button */}
                <button
                  onClick={() => { setShowAttachForm((v) => !v); }}
                  title="Attach clip or scenepack"
                  className="w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-150 flex-shrink-0"
                  style={{ background: showAttachForm || pendingAttachment ? 'var(--primary)' : 'var(--muted)', color: showAttachForm || pendingAttachment ? '#fff' : 'var(--muted-foreground)', border: '1px solid var(--border)' }}
                >
                  <Paperclip size={13} />
                </button>
                {/* Send button */}
                <button
                  onClick={handleSubmitComment}
                  disabled={!commentText.trim() && !pendingAttachment}
                  className="w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-150 disabled:opacity-40 flex-shrink-0"
                  style={{ background: 'var(--primary)', color: '#fff' }}
                >
                  <Send size={13} />
                </button>
              </div>

              {/* Hint text */}
              {!showAttachForm && !pendingAttachment && (
                <p className="text-[10px] mt-1.5" style={{ color: 'var(--muted-foreground)' }}>
                  <Paperclip size={9} className="inline mr-0.5" /> Attach a clip or scenepack as your response
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function RequestsContent() {
  const [requests, setRequests] = useState<ClipRequest[]>([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeTab, setActiveTab] = useState<'trending' | 'newest' | 'open'>('trending');
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const [newRequest, setNewRequest] = useState({ title: '', description: '', category: 'Action', tags: '' });

  const filtered = requests.filter((r) => activeCategory === 'All' || r.category === activeCategory);

  const sorted = [...filtered].sort((a, b) => {
    if (activeTab === 'trending') return b.likes - a.likes;
    if (activeTab === 'open') return a.status === 'open' ? -1 : 1;
    return 0;
  });

  function handleLike(id: string) {
    setRequests((prev) => prev.map((r) => r.id === id ? { ...r, liked: !r.liked, likes: r.liked ? r.likes - 1 : r.likes + 1 } : r));
  }

  function handleCommentLike(reqId: string, commentId: string) {
    setRequests((prev) => prev.map((r) => r.id !== reqId ? r : {
      ...r,
      comments: r.comments.map((c) => c.id !== commentId ? c : { ...c, liked: !c.liked, likes: c.liked ? c.likes - 1 : c.likes + 1 }),
    }));
  }

  function handleAddComment(reqId: string, text: string, attachment?: ClipAttachment) {
    setRequests((prev) => prev.map((r) => r.id !== reqId ? r : {
      ...r,
      commentCount: r.commentCount + 1,
      comments: [...r.comments, {
        id: `c-${Date.now()}`,
        author: 'You',
        avatar: '',
        verified: false,
        text,
        likes: 0,
        liked: false,
        timeAgo: 'just now',
        ...(attachment ? { attachment } : {}),
      }],
    }));
  }

  function handleSubmitRequest() {
    if (!newRequest.title.trim()) return;
    const req: ClipRequest = {
      id: `req-${Date.now()}`,
      author: 'You',
      avatar: '',
      verified: false,
      title: newRequest.title,
      description: newRequest.description,
      tags: newRequest.tags.split(',').map((t) => t.trim()).filter(Boolean),
      category: newRequest.category,
      likes: 0,
      liked: false,
      commentCount: 0,
      timeAgo: 'just now',
      status: 'open',
      comments: [],
    };
    setRequests((prev) => [req, ...prev]);
    setNewRequest({ title: '', description: '', category: 'Action', tags: '' });
    setShowNewRequestForm(false);
  }

  return (
    <main className="max-w-screen-md mx-auto px-4 pb-28 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight mb-1" style={{ color: 'var(--foreground)' }}>Clip Requests</h1>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            {requests.length === 0 ? 'No requests yet — be the first to post one!' : `${requests.length} request${requests.length !== 1 ? 's' : ''} from the community`}
          </p>
        </div>
        <button onClick={() => setShowNewRequestForm((v) => !v)} className="btn-primary flex items-center gap-2 px-4 py-2.5 text-sm">
          {showNewRequestForm ? <X size={14} /> : <Plus size={14} />}
          {showNewRequestForm ? 'Cancel' : 'New Request'}
        </button>
      </div>

      {/* New Request Form */}
      {showNewRequestForm && (
        <div className="rounded-2xl p-5 mb-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <h2 className="text-sm font-bold mb-4" style={{ color: 'var(--foreground)' }}>Post a Clip Request</h2>
          <div className="space-y-3">
            <input type="text" value={newRequest.title} onChange={(e) => setNewRequest((p) => ({ ...p, title: e.target.value }))} placeholder="What clip are you looking for?" className="input-field w-full py-2.5 px-3 text-sm" />
            <textarea value={newRequest.description} onChange={(e) => setNewRequest((p) => ({ ...p, description: e.target.value }))} placeholder="Describe what you need in detail…" rows={3} className="input-field w-full py-2.5 px-3 text-sm resize-none" />
            <div className="grid grid-cols-2 gap-3">
              <select value={newRequest.category} onChange={(e) => setNewRequest((p) => ({ ...p, category: e.target.value }))} className="input-field py-2.5 px-3 text-sm">
                {CATEGORIES.filter((c) => c !== 'All').map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input type="text" value={newRequest.tags} onChange={(e) => setNewRequest((p) => ({ ...p, tags: e.target.value }))} placeholder="Tags (comma separated)" className="input-field py-2.5 px-3 text-sm" />
            </div>
            <button onClick={handleSubmitRequest} disabled={!newRequest.title.trim()} className="btn-primary w-full py-2.5 text-sm disabled:opacity-40">Post Request</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-5 p-1 rounded-xl w-fit" style={{ background: 'var(--muted)' }}>
        {[
          { key: 'trending', label: 'Trending', icon: <Flame size={13} /> },
          { key: 'newest', label: 'Newest', icon: <Clock size={13} /> },
          { key: 'open', label: 'Open', icon: <Sparkles size={13} /> },
        ].map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key as typeof activeTab)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-150" style={{ background: isActive ? 'var(--card)' : 'transparent', color: isActive ? 'var(--foreground)' : 'var(--muted-foreground)', boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.3)' : 'none' }}>
              {tab.icon}
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Category chips */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 mb-5" style={{ scrollbarWidth: 'none' }}>
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className="category-chip flex items-center gap-1.5 flex-shrink-0"
            style={activeCategory === cat ? { background: 'var(--primary)', color: '#fff', borderColor: 'var(--primary)' } : {}}
          >
            <CategoryIcon category={cat} size={12} color={activeCategory === cat ? '#fff' : undefined} />
            {cat}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {sorted.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'var(--muted)' }}>
            <Film size={28} style={{ color: 'var(--muted-foreground)' }} />
          </div>
          <p className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>No requests yet</p>
          <p className="text-sm max-w-xs" style={{ color: 'var(--muted-foreground)' }}>
            Be the first to post a clip request! The community will respond with matching clips and scenepacks.
          </p>
          <button onClick={() => setShowNewRequestForm(true)} className="btn-primary px-5 py-2.5 text-sm mt-2 flex items-center gap-2">
            <Plus size={14} /> Post a Request
          </button>
        </div>
      )}

      {/* Requests list */}
      {sorted.length > 0 && (
        <div className="space-y-4">
          {sorted.map((req) => (
            <RequestCard key={req.id} request={req} onLike={handleLike} onCommentLike={handleCommentLike} onAddComment={handleAddComment} />
          ))}
        </div>
      )}
    </main>
  );
}
